import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Btn, Modal, Field, inputCls } from "@/components/common";
import { Plus, Pencil, Trash2, Building2, MapPin, BedDouble, X } from "lucide-react";

const emptyStructure = { name: "", atoll: "", island: "", image: "", tin: "", order: 0, maldive_autentiche: true, custom_commission: 0, staff_visible: true, agency_visible: true, occupancy_visible: true, availability_visible: true, esterna: false, allowed_agencies: [], rooms: [] };
const cloudOn = (s, k = "cloud_foto") => (s[k] === undefined || s[k] === null ? /blu lodge|vaagali/i.test(s.name || "") : !!s[k]);
const emptyRoom = { name: "", type: "", capacity: 2 };

export default function Proprieta() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyStructure);
  const [editId, setEditId] = useState(null);
  const [agencies, setAgencies] = useState([]);

  const load = () => api.get("/structures").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/users").then((r) => setAgencies([...new Set(r.data.filter((u) => u.role === "agency" && u.agency_name).map((u) => u.agency_name))])); }, []);
  const toggleAgency = (a) => setForm((f) => { const cur = f.allowed_agencies || []; return { ...f, allowed_agencies: cur.includes(a) ? cur.filter((x) => x !== a) : [...cur, a] }; });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setForm({ ...emptyStructure, order: items.length, rooms: [{ ...emptyRoom }] }); setEditId(null); setOpen(true); };
  const openEdit = (s) => { setForm({ ...emptyStructure, ...s, rooms: s.rooms?.length ? s.rooms : [] }); setEditId(s.id); setOpen(true); };

  const setRoom = (i, k, v) => setForm((f) => {
    const rooms = [...f.rooms];
    rooms[i] = { ...rooms[i], [k]: v };
    return { ...f, rooms };
  });
  const addRoom = () => setForm((f) => ({ ...f, rooms: [...f.rooms, { ...emptyRoom, _uid: Math.random().toString(36).slice(2) }] }));
  const delRoom = (i) => setForm((f) => ({ ...f, rooms: f.rooms.filter((_, idx) => idx !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form, order: Number(form.order),
      maldive_autentiche: form.maldive_autentiche !== false,
      staff_visible: form.staff_visible !== false,
      agency_visible: form.agency_visible !== false,
      occupancy_visible: form.occupancy_visible !== false,
      availability_visible: form.availability_visible !== false,
      esterna: form.esterna === true,
      cloud_foto: cloudOn(form),
      bar: cloudOn(form, "bar"),
      allowed_agencies: form.allowed_agencies || [],
      custom_commission: Number(form.custom_commission) || 0,
      rooms: form.rooms.map(({ _uid, ...r }) => ({ ...r, capacity: Number(r.capacity) || 0 })),
    };
    try {
      if (editId) await api.put(`/structures/${editId}`, payload);
      else await api.post("/structures", payload);
      toast.success("Proprietà salvata");
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Eliminare la proprietà e le sue camere?")) return;
    await api.delete(`/structures/${id}`);
    load();
  };

  return (
    <div>
      <PageHeader title="Proprietà" subtitle="Strutture della Blu Lodge Pvt Ltd e relative camere">
        <Btn data-testid="add-structure-btn" onClick={openNew}><Plus size={16} /> Nuova proprietà</Btn>
      </PageHeader>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="structures-grid">
        {items.map((s) => (
          <div key={s.id} className="bg-white border border-slate-200 rounded-md overflow-hidden">
            <div className="h-32 bg-slate-100">
              {s.image ? (
                <img src={s.image} alt={s.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full grid place-items-center text-slate-300"><Building2 size={36} /></div>
              )}
            </div>
            <div className="p-4">
              <div className="font-heading font-bold text-slate-900">{s.name}</div>
              <div className="text-xs text-slate-500 flex items-center gap-1 mt-1">
                <MapPin size={12} /> {s.island || "—"}{s.atoll ? `, ${s.atoll}` : ""}
              </div>
              <div className="text-xs text-slate-500 flex items-center gap-1 mt-2">
                <BedDouble size={13} /> {s.rooms?.length || 0} camere
              </div>
              <div className="flex gap-2 mt-4">
                <Btn variant="secondary" className="!py-1.5" onClick={() => openEdit(s)} data-testid={`edit-structure-${s.id}`}>
                  <Pencil size={14} /> Modifica
                </Btn>
                <button data-testid={`del-structure-${s.id}`} onClick={() => remove(s.id)} className="p-2 rounded-md border border-red-200 text-red-500 hover:bg-red-50">
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Modifica proprietà" : "Nuova proprietà"} wide>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Nome struttura">
            <input required value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} data-testid="s-name" />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Atollo">
              <input value={form.atoll} onChange={(e) => set("atoll", e.target.value)} className={inputCls} data-testid="s-atoll" />
            </Field>
            <Field label="Isola">
              <input value={form.island} onChange={(e) => set("island", e.target.value)} className={inputCls} data-testid="s-island" />
            </Field>
          </div>
          <Field label="Immagine (URL)">
            <input value={form.image} onChange={(e) => set("image", e.target.value)} className={inputCls} data-testid="s-image" />
          </Field>
          <Field label="TIN / Codice fiscale (per voucher)">
            <input value={form.tin || ""} onChange={(e) => set("tin", e.target.value)} className={inputCls} data-testid="s-tin" placeholder="Es. 1179844GST001" />
          </Field>

          <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-3">
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={form.maldive_autentiche !== false} onChange={(e) => set("maldive_autentiche", e.target.checked)} data-testid="s-maldive-autentiche" />
              Segue la politica <b>Maldive Autentiche</b>
            </label>
            {form.maldive_autentiche === false && (
              <Field label="Commissione personalizzata (€) — trattenuta senza versare a Maldive Autentiche">
                <input type="number" step="0.01" value={form.custom_commission || 0} onChange={(e) => set("custom_commission", e.target.value)} className={inputCls} data-testid="s-custom-commission" />
              </Field>
            )}
          </div>

          <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={form.staff_visible !== false} onChange={(e) => set("staff_visible", e.target.checked)} data-testid="s-staff-visible" />
              Visibile allo <b>staff</b> (Calendario sola lettura + Trasferimenti)
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700 mt-2">
              <input type="checkbox" checked={form.agency_visible !== false} onChange={(e) => set("agency_visible", e.target.checked)} data-testid="s-agency-visible" />
              Visibile alle <b>agenzie</b> (Calendario + creazione prenotazioni)
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700 mt-2">
              <input type="checkbox" checked={form.occupancy_visible !== false} onChange={(e) => set("occupancy_visible", e.target.checked)} data-testid="s-occupancy-visible" />
              Mostra in <b>Occupancy annuale</b>
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700 mt-2">
              <input type="checkbox" checked={form.availability_visible !== false} onChange={(e) => set("availability_visible", e.target.checked)} data-testid="s-availability-visible" />
              Mostra in <b>Disponibilità</b>
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700 mt-2">
              <input type="checkbox" checked={form.esterna === true} onChange={(e) => set("esterna", e.target.checked)} data-testid="s-esterna" />
              <b>Struttura esterna</b> (non gestita direttamente · ricevuta con acconto manuale)
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700 mt-2">
              <input type="checkbox" checked={cloudOn(form)} onChange={(e) => set("cloud_foto", e.target.checked)} data-testid="s-cloud-foto" />
              <b>Cloud foto</b> (le camere compaiono nell'elenco pagato / non pagato delle foto)
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700 mt-2">
              <input type="checkbox" checked={cloudOn(form, "bar")} onChange={(e) => set("bar", e.target.checked)} data-testid="s-bar" />
              <b>Bar e gadget</b> (le camere compaiono nella pagina del bar)
            </label>
            {form.agency_visible !== false && agencies.length > 0 && (
              <div className="mt-3 p-3 rounded-md border border-slate-200 bg-slate-50" data-testid="s-allowed-agencies">
                <div className="text-sm font-semibold text-slate-700">Agenzie che possono vedere e prenotare</div>
                <div className="text-xs text-slate-500 mb-2">Nessuna spunta = tutte le agenzie. Con una o più spunte, solo quelle agenzie (più admin e manager).</div>
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                  {agencies.map((a) => (
                    <label key={a} className="flex items-center gap-1.5 text-sm text-slate-700">
                      <input type="checkbox" checked={(form.allowed_agencies || []).includes(a)} onChange={() => toggleAgency(a)} data-testid={`s-agency-${a}`} /> {a}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-3">
            <div className="text-sm font-semibold text-slate-700">Costi guesthouse Keyodhoo (per notte)</div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Costo camera / notte">
                <div className="flex gap-2">
                  <input type="number" step="0.01" value={form.nightly_room_cost ?? 0} onChange={(e) => set("nightly_room_cost", e.target.value)} className={inputCls} data-testid="s-nightly-room" />
                  <select value={form.nightly_room_currency || "EUR"} onChange={(e) => set("nightly_room_currency", e.target.value)} className={inputCls} style={{ width: 92 }} data-testid="s-nightly-room-cur">
                    <option value="EUR">EUR</option>
                    <option value="USD">USD</option>
                  </select>
                </div>
              </Field>
              <Field label="Costo occupante >2 anni / notte">
                <div className="flex gap-2">
                  <input type="number" step="0.01" value={form.nightly_person_cost ?? 0} onChange={(e) => set("nightly_person_cost", e.target.value)} className={inputCls} data-testid="s-nightly-person" />
                  <select value={form.nightly_person_currency || "USD"} onChange={(e) => set("nightly_person_currency", e.target.value)} className={inputCls} style={{ width: 92 }} data-testid="s-nightly-person-cur">
                    <option value="EUR">EUR</option>
                    <option value="USD">USD</option>
                  </select>
                </div>
              </Field>
            </div>
            <p className="text-xs text-slate-400">Impostare 0 per Blu Lodge Maldives. Vale solo per le guesthouse di Keyodhoo. Gli importi vengono convertiti in EUR nei report.</p>
          </div>

          <div className="flex items-center justify-between pt-2">
            <span className="text-sm font-semibold text-slate-700">Camere ({form.rooms.length})</span>
            <Btn type="button" variant="secondary" className="!py-1.5" onClick={addRoom} data-testid="add-room-btn">
              <Plus size={14} /> Aggiungi camera
            </Btn>
          </div>
          <div className="space-y-2">
            <div className="grid grid-cols-[1fr_1fr_80px_32px] gap-2 text-xs text-slate-500 px-1">
              <span>Nome</span><span>Tipo</span><span>Posti</span><span></span>
            </div>
            {form.rooms.map((r, i) => (
              <div key={r._uid || r.id || i} className="grid grid-cols-[1fr_1fr_80px_32px] gap-2 items-center">
                <input value={r.name} onChange={(e) => setRoom(i, "name", e.target.value)} className={inputCls} placeholder="Room 101" data-testid={`room-name-${i}`} />
                <input value={r.type} onChange={(e) => setRoom(i, "type", e.target.value)} className={inputCls} placeholder="Tripla" />
                <input type="number" value={r.capacity} onChange={(e) => setRoom(i, "capacity", e.target.value)} className={inputCls} />
                <button type="button" onClick={() => delRoom(i)} className="p-1.5 text-slate-400 hover:text-red-600"><X size={16} /></button>
              </div>
            ))}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Btn type="button" variant="secondary" onClick={() => setOpen(false)}>Annulla</Btn>
            <Btn type="submit" data-testid="save-structure">Salva</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
